import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router';
import { routes } from '@/constants/routes.const';
import { IGlobalState, initialGlobalState } from '@/store';
import styled from 'styled-components';
import { verifyPassport } from '@/api';
import PassportInputForm from '@/components/PassportValidate/PassportInputForm';
import ReceiptLayout from '@/components/Receipt/ReceiptLayout';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import {
  IVerifyPassportPayload,
  IVerifyPassportResponse,
} from '@/types/common.type';
import { debounce } from 'lodash-es';
import { CASH_PICKUP_NATIONS } from '@repo/constants';
import * as Sentry from '@sentry/react';
import { isNationIncluded, isRefundableAirport } from '@repo/utils';

export interface IErrorState {
  message: string;
}
function PassportValidate() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    passportNumber: currPassportNumber,
    departureDate: currDepartureDate,
    airportRefundable,
    departureDate,
  } = useAppSelector((state) => state) as IGlobalState;

  const formMethod = useForm({
    defaultValues: {
      passportNumber: '',
      nationality: '',
    },
  });
  const { passportNumber } = formMethod.watch();
  const loginToken = localStorage.getItem('login');
  const departureDateToken = localStorage.getItem('departureDate');
  const customerNationToken = localStorage.getItem('customerNation');
  const [error, setError] = useState<IErrorState | undefined>();
  const isLogin = Boolean(localStorage.getItem('login'));

  const { mutate: passportMutation } = useMutation<
    IVerifyPassportResponse,
    AxiosError,
    IVerifyPassportPayload
  >((payload) => verifyPassport(payload), {
    retry: false,
    onSuccess: ({
      departureDate,
      register,
      customerNation,
      read,
      receiptUpload,
      uploadRequired,
      departureStatus,
      email,
      refundCondition,
      hasOverPrice,
      firstRefundDate,
      airportRefundable,
      refundedIn,
    }) => {
      departureDate && localStorage.setItem('departureDate', departureDate);
      customerNation && localStorage.setItem('customerNation', customerNation);
      dispatch({
        type: 'UPDATE_USER_INFO',
        payload: {
          passportNumber: passportNumber.toUpperCase(),
          departureDate,
          isRefundInfoRegistered: register,
          nationality: customerNation,
          haveSeenReceipt: read,
          isReceiptImageUploaded: receiptUpload,
          isUploadRequired: uploadRequired,
          isCustomerLeft: departureStatus === 'DEPARTURE_COMPLETE',
          showCatUi: false, //원래 showCatUi가 전달되어야하는데 서버 문제로 일단 false 고정으로 함
          email,
          refundCondition,
          refundAfter: refundCondition !== 'REFUND_ONLY',
          hasOverPrice,
          firstRefundDate,
          airportRefundable,
          isCashPickup:
            // refundedIn은 van 공항인지 키오스크인지 확인이 안될 때
            !refundedIn &&
            isNationIncluded(CASH_PICKUP_NATIONS, customerNation),
        },
      });
      Sentry.setUser({
        passportNumber: passportNumber.toUpperCase(),
        nationality: customerNation,
      });
      setError(undefined);
      localStorage.setItem('login', passportNumber);
      localStorage.setItem('korAccountSuccess', '0');

      if (airportRefundable) {
        if (uploadRequired) {
          return history.push(routes.departureCheck);
        }
        const selectedAirport = localStorage.getItem(passportNumber);
        if (!selectedAirport) return history.push(routes.selectAirport);
        if (isRefundableAirport(selectedAirport)) {
          return history.push(routes.airport);
        }
      }

      if (refundCondition === 'REFUND_ONLY') {
        history.push(routes.home);
        return;
      }

      if (departureDate) {
        history.push(routes.home);
        return;
      }
      history.push('/departure');
    },
    onError: ({ data }: any) => {
      setError({
        message: 'Please verify your Passport Number and try again.',
      });
      if (
        data.code === 'P0009' ||
        data.code === 'P0001' ||
        data.code === 'R0001'
      ) {
        dispatch({
          type: 'UPDATE_HOME_MODAL_STATE',
          payload: {
            isActive: true,
            type: 'CUSTOM',
            data: {
              title: 'Unable to Verify passport.',
              content: `${passportNumber.toUpperCase()}\n\nDid you enter passport number correctly?\nIf yes, press [Confirm] to continue,`,
              subBtnText: 'Cancel',
              btnText: 'Confirm',
              btnCallback: onClickCofirm,
            },
          },
        });
      }
    },
  });

  // 여러번 여권조회 중복호출 방지
  const debouncedMutation = useCallback(
    debounce((formattedPassportNumber) => {
      passportMutation({ passportNumber: formattedPassportNumber });
    }, 500),
    [passportMutation],
  );

  const onSubmitPassportNumber = () => {
    if (!passportNumber) {
      return;
    }
    const formattedPassportNumber = passportNumber
      .toUpperCase()
      .replace(/\s+/g, '');
    // 여권조회 호출
    debouncedMutation(formattedPassportNumber);
  };

  const onClickCofirm = () => {
    history.push(routes.refundInfoLeaflet, { passportNumber });
  };

  useEffect(() => {
    const handleBackButton = () => {
      if (currPassportNumber) {
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: initialGlobalState,
        });
        localStorage.removeItem('login');
        localStorage.removeItem('departureDate');
        localStorage.removeItem('customerNation');
      }
      window.history.go(1);
    };
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  useEffect(() => {
    if (customerNationToken === 'KOR') {
      return;
    }

    if (airportRefundable) {
      const isTransferWeb = localStorage.getItem(`${passportNumber}-transfer`);
      if (isTransferWeb === 'true') {
        if (departureDate) {
          history.push({
            pathname: routes.home,
          });
        } else {
          history.push({
            pathname: '/departure',
          });
        }
        return;
      }

      const selectedAirport = localStorage.getItem(passportNumber);

      if (!selectedAirport) return history.push(routes.selectAirport);
      if (isRefundableAirport(selectedAirport)) {
        return history.push(routes.airport);
      }
    }

    if (isMobile) {
      if (loginToken && departureDateToken) {
        history.push('/home');
      }
      if (isLogin) {
        history.push('/departure');
      }
    } else {
      if (currPassportNumber) {
        if (currDepartureDate) {
          history.push('/home');
        } else {
          history.push('/departure');
        }
      }
    }
  }, []);

  return (
    <ReceiptLayout>
      <Wrapper>
        <FormProvider {...formMethod}>
          <PassportInputForm
            onSubmit={onSubmitPassportNumber}
            {...(error && { error })}
          />
        </FormProvider>
      </Wrapper>
    </ReceiptLayout>
  );
}

export default PassportValidate;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px 0 100px;
`;
